<script setup lang="ts">
/**
 * Discord-Modal, originalgetreu nachgebaut (Dark Theme): dunkles Overlay,
 * zentrierte Karte, Titel + X, pro Feld ein 12px-Bold-Uppercase-Label mit
 * Pflicht-Stern und ein Input, Footer-Leiste mit „Cancel" (Link-Stil) und
 * Blurple-Submit. Der echte Bot öffnet genau so ein Modal: Titel = Name der
 * Kategorie, darunter die in der Kategorie aktivierten Inputs (Text kurz/lang,
 * vorgefertigte Integrations-Inputs wie die Stripe-Order-ID).
 *
 * Rein präsentational — der getippte Wert des ersten Feldes wird von der
 * Scroll-Timeline über `.dc-modal-value` sichtbar gemacht.
 */
interface ModalField {
  label: string
  placeholder?: string
  value?: string
  required?: boolean
  /** Paragraph-Input (mehrzeilig) statt einzeiliger Short-Input */
  paragraph?: boolean
}

const props = withDefaults(defineProps<{
  title: string
  /** Mehrere Felder — oder Einzelfeld über label/placeholder/value */
  fields?: ModalField[]
  label?: string
  placeholder?: string
  value?: string
  submitLabel?: string
  cancelLabel?: string
}>(), {
  fields: undefined,
  label: undefined,
  placeholder: '',
  value: '',
  submitLabel: 'Submit',
  cancelLabel: 'Cancel'
})

const resolvedFields = computed<ModalField[]>(() =>
  props.fields ?? [{ label: props.label ?? '', placeholder: props.placeholder, value: props.value, required: true }]
)
</script>

<template>
  <div class="dc-modal-overlay">
    <div class="dc-modal">
      <div class="dc-modal-header">
        <div class="dc-modal-title">{{ title }}</div>
        <UIcon name="i-lucide-x" class="dc-modal-close size-[22px]" aria-hidden="true" />
      </div>
      <div class="dc-modal-body">
        <div v-for="(f, i) in resolvedFields" :key="f.label" class="dc-modal-field">
          <label class="dc-modal-label">
            {{ f.label }}
            <span v-if="f.required !== false" class="dc-modal-required">*</span>
          </label>
          <div class="dc-modal-input" :class="{ 'is-paragraph': f.paragraph, 'is-first': i === 0 }">
            <span class="dc-modal-value" :class="{ 'dc-modal-value-typed': i === 0 }">{{ f.value }}</span>
            <span v-if="i === 0" class="dc-modal-caret" aria-hidden="true" />
            <span v-if="!f.value" class="dc-modal-placeholder">{{ f.placeholder }}</span>
          </div>
        </div>
      </div>
      <div class="dc-modal-footer">
        <span class="dc-modal-cancel">{{ cancelLabel }}</span>
        <span class="dc-modal-submit">{{ submitLabel }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dc-modal-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dc-overlay);
  font-family: var(--font-discord);
}

.dc-modal {
  width: min(440px, calc(100% - 32px));
  background: var(--dc-bg-hover);
  border: 1px solid var(--dc-border-card);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.55);
}

.dc-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 16px 0;
}

.dc-modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--dc-ink-header);
  line-height: 1.25;
}

.dc-modal-close {
  color: var(--dc-ink-muted);
  flex-shrink: 0;
  margin: -2px -2px 0 12px;
}

.dc-modal-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.dc-modal-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--dc-ink-label);
  margin-bottom: 8px;
}

.dc-modal-required {
  color: var(--dc-red);
  margin-left: 2px;
}

.dc-modal-input {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 40px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: 6px;
  padding: 9px 12px;
  font-size: 0.95rem;
  color: var(--dc-ink);
}

.dc-modal-input.is-first {
  border-color: var(--dc-blurple);
}

.dc-modal-input.is-paragraph {
  min-height: 84px;
  align-items: flex-start;
}

.dc-modal-value {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.dc-modal-caret {
  display: inline-block;
  width: 1px;
  height: 1.1em;
  background: var(--dc-ink);
  margin-left: 1px;
  animation: dc-caret 1s steps(1) infinite;
}

@keyframes dc-caret {
  50% {
    opacity: 0;
  }
}

.dc-modal-placeholder {
  position: absolute;
  left: 12px;
  top: 9px;
  color: var(--dc-ink-channel);
  pointer-events: none;
}

.dc-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px;
  background: var(--dc-bg-sidebar);
}

.dc-modal-cancel {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--dc-ink-header);
}

.dc-modal-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  min-height: 38px;
  padding: 2px 16px;
  background: var(--dc-blurple);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 8px;
}
</style>
